import type { ZodError, ZodType } from '@core/utils';
import type {
  FieldError,
  FieldErrors,
  FieldValues,
  ResolverResult,
} from 'react-hook-form';

/**
 * * Zod v4용 커스텀 resolver
 * * @hookform/resolvers 모듈의 현재 버전은 Zod v4를 지원하지 않으므로 직접 구현
 */
export const zodResolver =
  <TFieldValues extends FieldValues>(schema: ZodType) =>
  async (values: TFieldValues): Promise<ResolverResult<TFieldValues>> => {
    try {
      const result = await schema.safeParseAsync(values);

      if (result.success) {
        return {
          values: result.data as TFieldValues,
          errors: {},
        };
      } else {
        return {
          values: {},
          errors: zodToHookFormErrors(
            result.error,
          ) as FieldErrors<TFieldValues>,
        };
      }
    } catch (error) {
      console.error('Resolver error: ', error);

      return {
        values: {},
        errors: {
          root: {
            type: 'unknown',
            message: 'An unknown error occurred during validation',
          } as FieldError,
        } as FieldErrors<TFieldValues>,
      };
    }
  };

const zodToHookFormErrors = (zodError: ZodError): FieldErrors<FieldValues> => {
  const errors = {} as FieldErrors<FieldValues>;

  for (const issue of zodError.issues) {
    const path = issue.path.filter(
      (key): key is string | number =>
        typeof key === 'string' || typeof key === 'number',
    );

    setNestedError(errors, path, {
      type: issue.code,
      message: issue.message,
    });
  }

  return errors;
};

const setNestedError = <T extends Record<string | number, unknown>, V>(
  obj: T,
  path: (string | number)[],
  value: V,
): void => {
  let curr: Record<string | number, unknown> = obj;
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i];
    if (!(key in curr)) curr[key] = {};
    curr = curr[key] as Record<string | number, unknown>;
  }
  curr[path[path.length - 1]] = value;
};

export type { ZodError, ZodType } from '@core/utils';
export { z } from '@core/utils';
